import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase, ClickAwayListener, Tooltip } from '@material-ui/core';
import tokens from '../theme/tokens';

// 물품 한 줄 — 아이콘 · 이름 · [?] 도움말 · 수량 스테퍼( − n + ). 값은 스토어와 같은 문자열("0", "3")로 주고받는다.
const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 64,
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.lineSoft}`,
    '&:last-child': { borderBottom: 'none' },
  },
  icon: {
    flex: 'none',
    width: 48,
    height: 48,
    marginRight: 14,
    objectFit: 'contain',
  },
  label: {
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: -0.6,
    color: tokens.text,
    wordBreak: 'keep-all',
  },
  help: {
    flex: 'none',
    width: 22,
    height: 22,
    marginLeft: 6,
    borderRadius: 11,
    border: `1px solid ${tokens.line}`,
    color: tokens.muted,
    fontSize: 12,
    fontWeight: 700,
    '&:hover, &$helpOn': { borderColor: tokens.primary, color: tokens.primaryDark, backgroundColor: tokens.primarySoft },
  },
  helpOn: {},
  tip: {
    maxWidth: 360,
    padding: '14px 18px',
    backgroundColor: tokens.surface,
    color: tokens.text,
    borderRadius: 12,
    boxShadow: '0 5px 15px 0 rgba(202, 202, 207, 0.36)',
    fontSize: 13,
  },
  tipArrow: { color: tokens.surface },
  stepper: {
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    height: 40,
    borderRadius: 20,
    border: `1px solid ${tokens.line}`,
    backgroundColor: tokens.surface,
    overflow: 'hidden',
    transition: 'border-color .12s, box-shadow .12s',
    '&$active': { borderColor: tokens.primary },
    '&:focus-within': { borderColor: tokens.primary, boxShadow: tokens.shadow },
  },
  active: {},
  step: {
    width: 40,
    height: 40,
    color: tokens.text,
    fontSize: 20,
    lineHeight: 1,
    '&:hover': { backgroundColor: tokens.primarySoft, color: tokens.primaryDark },
    '&$disabled': { color: tokens.placeholder, backgroundColor: 'transparent' },
  },
  disabled: {},
  input: {
    width: 44,
    height: 38,
    padding: 0,
    border: 'none',
    outline: 'none',
    textAlign: 'center',
    fontSize: 16,
    fontWeight: 700,
    fontFamily: 'inherit',
    color: tokens.ink,
    backgroundColor: 'transparent',
    '&$zero': { color: tokens.placeholder, fontWeight: 500 },
    '-moz-appearance': 'textfield',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { '-webkit-appearance': 'none', margin: 0 },
  },
  zero: {},
  dense: {
    minHeight: 56,
    '& $icon': { width: 40, height: 40, marginRight: 10 },
    '& $label': { fontSize: 14 },
    '& $stepper': { height: 36 },
    '& $step': { width: 36, height: 36 },
    '& $input': { width: 40, height: 34, fontSize: 15 },
  },
}));

const clamp = (n, max) => Math.max(0, Math.min(max, n));

function StuffCounter({ item, value, max = 999, onChange, renderHelp, onHelp, dense = false }) {
  const cs = useStyles();
  const [tipOpen, setTipOpen] = useState(false);
  const n = parseInt(value, 10) || 0;

  const commit = (next) => onChange(item.name, String(clamp(next, max)));
  const handleInput = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    commit(digits === '' ? 0 : parseInt(digits, 10));
  };
  const handleHelp = () => {
    if (onHelp) onHelp(item);
    else setTipOpen((v) => !v);
  };

  const helpButton = (
    <ButtonBase className={clsx(cs.help, tipOpen && cs.helpOn)} onClick={handleHelp} aria-label={`${item.title} 기준`} focusRipple={false}>?</ButtonBase>
  );

  return (
    <div className={clsx(cs.root, dense && cs.dense)}>
      {item.icon && <img className={cs.icon} alt="" src={item.icon} />}
      <div className={cs.label}>
        <span>{item.title}</span>
        {(renderHelp || onHelp) && (
          renderHelp
            ? (
              <ClickAwayListener onClickAway={() => setTipOpen(false)}>
                <Tooltip
                  arrow
                  placement="top-start"
                  open={tipOpen}
                  onClose={() => setTipOpen(false)}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  classes={{ tooltip: cs.tip, arrow: cs.tipArrow }}
                  title={renderHelp(item)}
                >
                  {helpButton}
                </Tooltip>
              </ClickAwayListener>
            )
            : helpButton
        )}
      </div>
      <div className={clsx(cs.stepper, n > 0 && cs.active)}>
        <ButtonBase className={clsx(cs.step, n <= 0 && cs.disabled)} disabled={n <= 0} onClick={() => commit(n - 1)} aria-label="수량 줄이기" focusRipple={false}>−</ButtonBase>
        <input
          className={clsx(cs.input, n === 0 && cs.zero)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={n}
          onChange={handleInput}
          onFocus={(e) => e.target.select()}
          aria-label={`${item.title} 수량`}
        />
        <ButtonBase className={clsx(cs.step, n >= max && cs.disabled)} disabled={n >= max} onClick={() => commit(n + 1)} aria-label="수량 늘리기" focusRipple={false}>+</ButtonBase>
      </div>
    </div>
  );
}

export default StuffCounter;
